import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { OrderStatus } from '../../types';
import { formatPrice, formatDate } from '../../utils';
import { statusText, statusClass, NEXT_STATUSES } from '../../utils/orderStatus';
import { deliveryLabel } from '../../utils/delivery';
import { ordersAPI } from '../../services/api';
import AdminProducts from './AdminProducts';
import AdminUsers from './AdminUsers';
import AdminPickupLocations from './AdminPickupLocations';
import AdminAuditLogs from './AdminAuditLogs';
import AdminOrderDrawer from './AdminOrderDrawer';
import RevenueChart from './RevenueChart';
import BrandRevenue, { brandSharesOfOrder } from './BrandRevenue';
import '../../styles/admin.css';
import '../../styles/profile.css';

type AdminTab = 'orders' | 'products' | 'accounts' | 'pickups' | 'audit';

export interface ApiOrderItem {
  id: number;
  product_id: string;
  sku_id?: string;
  quantity: number;
  price: number;
  product?: {
    id: string;
    name: string;
    main_image?: string;
    brand?: { id: string; name: string } | null;
  };
  sku?: {
    id: string;
    flavor?: string;
    spec?: string;
    unit?: string;
  };
}

export interface ApiOrder {
  id: string;
  user_id: string;
  status: OrderStatus;
  payment_type?: 'normal' | 'monthly';
  delivery_method?: string;
  subtotal: number;
  discount: number;
  invoice?: { tax_id: string; company_name: string } | null;
  buyer?: {
    username?: string;
    email?: string;
    company_name?: string | null;
    contact_name?: string | null;
    contact_phone?: string | null;
    tax_id?: string | null;
  } | null;
  admin_note?: string | null;
  shipping_fee: number;
  total_amount: number;
  locked: boolean;
  paid_at?: string | null;
  completed_at?: string | null;
  payment_deadline?: string | null;
  shipping_info: {
    name?: string;
    phone?: string;
    city?: string;
    postalCode?: string;
    address?: string;
    store_name?: string;
    pickup_location_id?: string;
    location_name?: string;
    contact?: string;
    note?: string;
  };
  payment_info?: {
    last5Digits: string;
    completedAt: string;
  };
  created_at: string;
  updated_at: string;
  items: ApiOrderItem[];
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AdminTab>('orders');
  const [orders, setOrders] = useState<ApiOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<ApiOrder | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  // 時間段篩選（依下單日期，YYYY-MM-DD；空字串代表不限）
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [showRevenueChart, setShowRevenueChart] = useState(false);
  const [revenueBrand, setRevenueBrand] = useState<string | null>(null);
  const PAGE_SIZE = 20;

  // 篩選或搜尋改變時回到第 1 頁
  useEffect(() => {
    setPage(1);
  }, [statusFilter, search, dateFrom, dateTo]);

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem('user') || 'null');

    if (!currentUser || !currentUser.isAdmin) {
      alert('您沒有權限訪問此頁面');
      navigate('/');
      return;
    }

    fetchOrders();
  }, [navigate]);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ordersAPI.getAllOrders();
      setOrders(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || '無法載入訂單，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId: string, newStatus: OrderStatus) => {
    setUpdatingId(orderId);
    try {
      const updated = await ordersAPI.changeStatus(orderId, newStatus);
      setOrders(prev =>
        prev.map(order => (order.id === orderId ? { ...order, ...updated } : order))
      );
      setSelectedOrder(prev =>
        prev?.id === orderId ? { ...prev, ...updated } : prev
      );
    } catch (err: any) {
      alert(err.response?.data?.detail || '更新狀態失敗，請再試一次');
    } finally {
      setUpdatingId(null);
    }
  };

  const updateOrderItems = async (
    orderId: string,
    items: Array<{ sku_id: string; quantity: number }>,
    discount?: number,
  ) => {
    setUpdatingId(orderId);
    try {
      const updated = await ordersAPI.updateItems(orderId, items, discount);
      setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, ...updated } : o)));
      setSelectedOrder(prev => (prev?.id === orderId ? { ...prev, ...updated } : prev));
    } catch (err: any) {
      alert(err.response?.data?.detail || '品項調整失敗，請再試一次');
    } finally {
      setUpdatingId(null);
    }
  };

  const saveAdminNote = async (orderId: string, note: string) => {
    setUpdatingId(orderId);
    try {
      const updated = await ordersAPI.updateAdminNote(orderId, note);
      setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, ...updated } : o)));
      setSelectedOrder(prev => (prev?.id === orderId ? { ...prev, ...updated } : prev));
    } catch (err: any) {
      alert(err.response?.data?.detail || '備註儲存失敗，請再試一次');
    } finally {
      setUpdatingId(null);
    }
  };

  const verifyOrder = async (
    orderId: string,
    shippingFee: number,
    discount = 0,
    paymentType: 'normal' | 'monthly' = 'normal',
  ) => {
    setUpdatingId(orderId);
    try {
      const updated = await ordersAPI.verify(orderId, shippingFee, discount, paymentType);
      setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, ...updated } : o)));
      setSelectedOrder(prev => (prev?.id === orderId ? { ...prev, ...updated } : prev));
    } catch (err: any) {
      alert(err.response?.data?.detail || '核對失敗，請再試一次');
    } finally {
      setUpdatingId(null);
    }
  };

  const rollbackOrder = async (orderId: string) => {
    setUpdatingId(orderId);
    try {
      const updated = await ordersAPI.rollback(orderId);
      setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, ...updated } : o)));
      setSelectedOrder(prev => (prev?.id === orderId ? { ...prev, ...updated } : prev));
    } catch (err: any) {
      alert(err.response?.data?.detail || '退回失敗，請再試一次');
    } finally {
      setUpdatingId(null);
    }
  };

  const adjustMonthlyAmount = async (orderId: string, shippingFee: number, discount: number) => {
    setUpdatingId(orderId);
    try {
      const updated = await ordersAPI.adjustAmount(orderId, shippingFee, discount);
      setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, ...updated } : o)));
      setSelectedOrder(prev => (prev?.id === orderId ? { ...prev, ...updated } : prev));
    } catch (err: any) {
      alert(err.response?.data?.detail || '金額調整失敗，請再試一次');
    } finally {
      setUpdatingId(null);
    }
  };

  const getStatusText = statusText;
  const getStatusClass = statusClass;

  // 需要管理員處理的狀態
  const ACTIONABLE = ['pending_review', 'pending_confirm'];

  const STATUS_FILTERS = [
    { key: 'all', label: '全部' },
    { key: 'pending_review', label: '等待核對' },
    { key: 'pending_payment', label: '等待付款' },
    { key: 'pending_confirm', label: '等待入帳確認' },
    { key: 'preparing', label: '準備出貨' },
    { key: 'completed', label: '已完成' },
    { key: 'cancelled', label: '已取消' },
    { key: 'expired', label: '已逾期' },
  ];

  // 時間段快捷選項
  const toYMD = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const applyPreset = (preset: 'today' | 'thisMonth' | 'lastMonth' | 'last30' | 'all') => {
    const now = new Date();
    if (preset === 'all') { setDateFrom(''); setDateTo(''); return; }
    if (preset === 'today') { const t = toYMD(now); setDateFrom(t); setDateTo(t); return; }
    if (preset === 'thisMonth') {
      setDateFrom(toYMD(new Date(now.getFullYear(), now.getMonth(), 1)));
      setDateTo(toYMD(now));
      return;
    }
    if (preset === 'lastMonth') {
      setDateFrom(toYMD(new Date(now.getFullYear(), now.getMonth() - 1, 1)));
      setDateTo(toYMD(new Date(now.getFullYear(), now.getMonth(), 0)));
      return;
    }
    const from = new Date(now); from.setDate(now.getDate() - 29);
    setDateFrom(toYMD(from)); setDateTo(toYMD(now));
  };
  const hasDateRange = Boolean(dateFrom || dateTo);
  const dateRangeLabel = !hasDateRange
    ? '全部期間'
    : `${dateFrom || '最早'} ～ ${dateTo || '今天'}`;

  // 以本地日期比對（含起訖當天）
  const ymdInRange = (iso: string) => {
    if (!hasDateRange) return true;
    const d = toYMD(new Date(iso));
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  };
  // 已完成訂單依「完成日期」篩選（營收以此計算），其餘依下單日期
  const inDateRange = (o: ApiOrder) =>
    o.status === 'completed'
      ? ymdInRange(o.completed_at || o.updated_at)
      : ymdInRange(o.created_at);
  // 時間段內的訂單：統計卡、狀態數量、列表皆以此為準
  const rangeOrders = orders.filter(inDateRange);

  // 由新到舊
  const sortedOrders = [...rangeOrders].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const statusCounts = rangeOrders.reduce<Record<string, number>>((m, o) => {
    m[o.status] = (m[o.status] || 0) + 1;
    return m;
  }, {});

  const q = search.trim().toLowerCase();
  const filteredOrders = sortedOrders.filter(o => {
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (!q) return true;
    return (
      o.id.toLowerCase().includes(q) ||
      (o.shipping_info.name || '').toLowerCase().includes(q) ||
      (o.shipping_info.phone || '').includes(q)
    );
  });

  const pendingCount = rangeOrders.filter(o => ACTIONABLE.includes(o.status)).length;

  // 待審核超過 3 天視為滯留，提醒管理員處理
  const STALE_REVIEW_DAYS = 3;
  const isStaleReview = (o: ApiOrder) =>
    o.status === 'pending_review' &&
    Date.now() - new Date(o.created_at).getTime() > STALE_REVIEW_DAYS * 24 * 60 * 60 * 1000;
  const staleReviewCount = orders.filter(isStaleReview).length;
  const preparingCount = rangeOrders.filter(o => o.status === 'preparing').length;
  const completedOrders = rangeOrders.filter(o => o.status === 'completed');
  const completedRevenue = completedOrders.reduce((total, o) => total + o.total_amount, 0);
  // 趨勢圖：選了品牌就只畫該品牌在各訂單的營業額（區間內沒有該品牌時視同未選）
  const revenueBrandName = revenueBrand
    ? completedOrders.map(o => brandSharesOfOrder(o).get(revenueBrand)?.name).find(Boolean)
    : undefined;
  const activeRevenueBrand = revenueBrandName ? revenueBrand : null;
  const revenuePoints = completedOrders.flatMap(o => {
    const at = o.completed_at || o.updated_at;
    if (!activeRevenueBrand) return [{ at, amount: o.total_amount }];
    const share = brandSharesOfOrder(o).get(activeRevenueBrand);
    return share ? [{ at, amount: share.amount }] : [];
  });

  // 分頁
  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedOrders = filteredOrders.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const rangeStart = filteredOrders.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, filteredOrders.length);

  return (
    <div className="admin-dashboard">
      <div className="container">
        <h1>後台管理</h1>

        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            訂單管理
          </button>
          <button
            className={`admin-tab ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            商品管理
          </button>
          <button
            className={`admin-tab ${activeTab === 'accounts' ? 'active' : ''}`}
            onClick={() => setActiveTab('accounts')}
          >
            帳號管理
          </button>
          <button
            className={`admin-tab ${activeTab === 'pickups' ? 'active' : ''}`}
            onClick={() => setActiveTab('pickups')}
          >
            自取點
          </button>
          <button
            className={`admin-tab ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            操作紀錄
          </button>
        </div>

        {activeTab === 'products' && <AdminProducts />}

        {activeTab === 'accounts' && <AdminUsers />}

        {activeTab === 'pickups' && <AdminPickupLocations />}

        {activeTab === 'audit' && <AdminAuditLogs />}

        {activeTab === 'orders' && <>
        {staleReviewCount > 0 && (
          <div
            className="stale-review-banner"
            role="alert"
            onClick={() => setStatusFilter('pending_review')}
          >
            <span className="stale-review-dot" aria-hidden="true" />
            {staleReviewCount} 筆訂單待核對已超過 {STALE_REVIEW_DAYS} 天，庫存持續被保留中 — 點擊查看
          </div>
        )}
        <div className="date-range-bar">
          <span className="date-range-title">時間段</span>
          <div className="date-range-presets">
            {([
              ['all', '全部'],
              ['today', '今天'],
              ['thisMonth', '本月'],
              ['lastMonth', '上月'],
              ['last30', '近 30 天'],
            ] as const).map(([key, label]) => (
              <button key={key} className="filter-chip" onClick={() => applyPreset(key)}>{label}</button>
            ))}
          </div>
          <div className="date-range-inputs">
            <input type="date" className="date-input" value={dateFrom} max={dateTo || undefined}
              onChange={e => setDateFrom(e.target.value)} aria-label="起始日期" />
            <span className="date-range-sep">～</span>
            <input type="date" className="date-input" value={dateTo} min={dateFrom || undefined}
              onChange={e => setDateTo(e.target.value)} aria-label="結束日期" />
            {hasDateRange && (
              <button className="date-range-clear" onClick={() => applyPreset('all')}>清除</button>
            )}
          </div>
        </div>

        <div className="admin-stats">
          <div className="stat-card clickable" onClick={() => setStatusFilter('all')}>
            <h3>總訂單數</h3>
            <div className="stat-number">{rangeOrders.length}</div>
            <span className="stat-sub">{dateRangeLabel}</span>
          </div>
          <div
            className={`stat-card clickable ${pendingCount > 0 ? 'stat-card-alert' : ''}`}
            onClick={() => setStatusFilter('pending_review')}
          >
            <h3>待處理</h3>
            <div className="stat-number">{pendingCount}</div>
            <span className="stat-sub">等待核對 / 入帳確認</span>
          </div>
          <div className="stat-card clickable" onClick={() => setStatusFilter('preparing')}>
            <h3>準備出貨</h3>
            <div className="stat-number">{preparingCount}</div>
          </div>
          <div
            className={`stat-card clickable ${showRevenueChart ? 'stat-card-active' : ''}`}
            onClick={() => { setStatusFilter('completed'); setShowRevenueChart(v => !v); }}
          >
            <h3>已完成營收</h3>
            <div className="stat-number">{formatPrice(completedRevenue)}</div>
            <span className="stat-sub">{dateRangeLabel}・{completedOrders.length} 筆（依完成日期）</span>
            <span className="stat-sub stat-hint">{showRevenueChart ? '點擊收合圖表' : '點擊查看趨勢圖'}</span>
          </div>
        </div>

        {showRevenueChart && (
          <>
            <RevenueChart
              points={revenuePoints}
              title={revenueBrandName ? `已完成營收趨勢・${revenueBrandName}` : undefined}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onClose={() => setShowRevenueChart(false)}
            />
            <BrandRevenue
              orders={completedOrders}
              selectedBrand={activeRevenueBrand}
              onSelect={setRevenueBrand}
            />
          </>
        )}

        <div className="orders-table">
          <div className="orders-toolbar">
            <div className="status-filter-chips">
              {STATUS_FILTERS.map(f => (
                <button
                  key={f.key}
                  className={`filter-chip ${statusFilter === f.key ? 'active' : ''}`}
                  onClick={() => setStatusFilter(f.key)}
                >
                  {f.label}
                  <span className="chip-count">
                    {f.key === 'all' ? rangeOrders.length : (statusCounts[f.key] || 0)}
                  </span>
                </button>
              ))}
            </div>
            <input
              className="orders-search"
              type="text"
              placeholder="搜尋訂單編號 / 客戶姓名 / 電話"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="empty-orders">
              <p>載入中...</p>
            </div>
          ) : error ? (
            <div className="empty-orders">
              <p style={{ color: '#e74c3c' }}>{error}</p>
              <button onClick={fetchOrders} style={{ marginTop: '12px', padding: '8px 16px', cursor: 'pointer' }}>
                重新載入
              </button>
            </div>
          ) : orders.length === 0 ? (
            <div className="empty-orders">
              <p>目前沒有訂單</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="empty-orders">
              <p>沒有符合條件的訂單</p>
              <button
                onClick={() => { setStatusFilter('all'); setSearch(''); }}
                style={{ marginTop: 12, padding: '8px 16px', cursor: 'pointer' }}
              >
                清除篩選
              </button>
            </div>
          ) : (
            <>
              {/* 桌面版表格 */}
              <div className="table-container desktop-only">
                <table>
                  <thead>
                    <tr>
                      <th>訂單編號</th>
                      <th>下單日期</th>
                      <th>客戶資訊</th>
                      <th>商品</th>
                      <th>金額</th>
                      <th>狀態 / 付款</th>
                      <th>更改狀態</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedOrders.map(order => (
                      <tr
                        key={order.id}
                        className={`clickable-row ${ACTIONABLE.includes(order.status) ? 'needs-action' : ''} ${isStaleReview(order) ? 'stale-review' : ''}`}
                        onClick={() => setSelectedOrder(order)}
                      >
                        <td>
                          <span className="order-id" title={order.id}>
                            {order.id.length > 10 ? `${order.id.slice(0, 8)}…` : order.id}
                          </span>
                          {isStaleReview(order) && (
                            <span className="stale-badge">
                              逾 {Math.floor((Date.now() - new Date(order.created_at).getTime()) / 86400000)} 天未核對
                            </span>
                          )}
                        </td>
                        <td>{formatDate(order.created_at)}</td>
                        <td>
                          <div className="customer-info">
                            {order.buyer && (
                              <div className="buyer-line">
                                <strong>{order.buyer.company_name || order.buyer.username}</strong>
                                {order.buyer.company_name && <span className="phone"> @{order.buyer.username}</span>}
                              </div>
                            )}
                            <div>{order.shipping_info.name}</div>
                            <div className="phone">{order.shipping_info.phone}</div>
                            <div className="address">
                              <span className="category-badge">{deliveryLabel(order.delivery_method)}</span>{' '}
                              {order.delivery_method === 'cvs_711'
                                ? order.shipping_info.store_name || ''
                                : order.delivery_method === 'self_pickup'
                                ? order.shipping_info.location_name
                                : `${order.shipping_info.city || ''} ${order.shipping_info.address || ''}`}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="order-items">
                            {order.items.map((item, index) => (
                              <div key={index} className="item">
                                {item.product?.name || item.product_id} x {item.quantity}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="amount">{formatPrice(order.total_amount)}</td>
                        <td>
                          <span className={`status ${getStatusClass(order.status)}`}>
                            {getStatusText(order.status)}
                          </span>
                          {order.payment_type === 'monthly' && (
                            <span className="status status-confirmed" style={{ marginLeft: 4 }}>月結</span>
                          )}
                          {order.payment_info && (
                            <div className="admin-payment-info">
                              <small>
                                轉帳末五碼: <strong>{order.payment_info.last5Digits}</strong>
                              </small>
                              <small>
                                提交時間: {formatDate(order.payment_info.completedAt)}
                              </small>
                            </div>
                          )}
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <div className="status-controls">
                            {NEXT_STATUSES[order.status]?.length > 0 ? (
                              <select
                                value=""
                                onChange={(e) => e.target.value && updateOrderStatus(order.id, e.target.value as OrderStatus)}
                                className="status-select"
                                disabled={updatingId === order.id}
                              >
                                <option value="">變更為…</option>
                                {NEXT_STATUSES[order.status].map(s => (
                                  <option key={s} value={s}>{getStatusText(s)}</option>
                                ))}
                              </select>
                            ) : (
                              <span className="updating-label">—</span>
                            )}
                            {updatingId === order.id && (
                              <span className="updating-label">更新中...</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 手機版卡片列表 */}
              <div className="mobile-order-list mobile-only">
                {pagedOrders.map(order => (
                  <div
                    key={order.id}
                    className={`mobile-order-card ${ACTIONABLE.includes(order.status) ? 'needs-action' : ''}`}
                    onClick={() => setSelectedOrder(order)}
                  >
                    <div className="moc-top">
                      <span className="order-id" title={order.id}>
                        #{order.id.length > 10 ? `${order.id.slice(0, 8)}…` : order.id}
                      </span>
                      <span className={`status ${getStatusClass(order.status)}`}>
                        {getStatusText(order.status)}
                      </span>
                      {order.payment_type === 'monthly' && (
                        <span className="status status-confirmed">月結</span>
                      )}
                    </div>
                    <div className="moc-middle">
                      <span className="moc-name">
                        {order.buyer ? (order.buyer.company_name || order.buyer.username) : order.shipping_info.name}
                      </span>
                      <span className="moc-amount">{formatPrice(order.total_amount)}</span>
                    </div>
                    <div className="moc-bottom">
                      <span className="moc-date">{formatDate(order.created_at)}</span>
                      <span className="moc-items">
                        {order.items.map(i => i.product?.name || i.product_id).join('、')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* 分頁 */}
              <div className="orders-pagination">
                <span className="pagination-summary">
                  顯示 {rangeStart}–{rangeEnd}，共 {filteredOrders.length} 筆
                </span>
                {totalPages > 1 && (
                  <div className="pagination-controls">
                    <button
                      className="page-btn"
                      disabled={currentPage === 1}
                      onClick={() => setPage(currentPage - 1)}
                    >
                      上一頁
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                      .reduce<(number | string)[]>((acc, p, idx, arr) => {
                        if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('…');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, i) =>
                        typeof p === 'number' ? (
                          <button
                            key={i}
                            className={`page-btn ${p === currentPage ? 'active' : ''}`}
                            onClick={() => setPage(p)}
                          >
                            {p}
                          </button>
                        ) : (
                          <span key={i} className="page-ellipsis">…</span>
                        )
                      )}
                    <button
                      className="page-btn"
                      disabled={currentPage === totalPages}
                      onClick={() => setPage(currentPage + 1)}
                    >
                      下一頁
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        </>}
      </div>

      <AdminOrderDrawer
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
        onStatusChange={updateOrderStatus}
        onVerify={verifyOrder}
        onAdjustAmount={adjustMonthlyAmount}
        onRollback={rollbackOrder}
        onSaveNote={saveAdminNote}
        onUpdateItems={updateOrderItems}
        updatingId={updatingId}
      />
    </div>
  );
};

export default AdminDashboard;
